import React, { forwardRef, useEffect, useRef, useState } from 'react';
import type { TextInputProps } from 'react-native';
import { Animated, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Colors from 'src/core/utils/Colors';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import useShake from 'src/presentation/hooks/useShake';

interface FormFieldProps extends TextInputProps {
	label: string;
	rightElement?: React.ReactNode;
	required?: boolean;
	error?: string;
	hint?: string;
}

const FormField = forwardRef<TextInput, FormFieldProps>(
	({ label, rightElement, required, error, hint, ...inputProps }, ref) => {
		const [focused, setFocused] = useState(false);
		const innerRef = useRef<TextInput>(null);
		const { translateX: shakeX, shake } = useShake();
		const disabled = inputProps.editable === false;

		// Bind zowel onze interne ref (tap-to-focus) als een eventueel doorgegeven ref aan de TextInput.
		const setRef = (node: TextInput | null) => {
			innerRef.current = node;
			if (typeof ref === 'function') {
				ref(node);
			} else if (ref) {
				ref.current = node;
			}
		};

		// Hide the error while the field is focused (the user is fixing it); it reappears on blur if still set.
		const showError = !!error && !focused;

		// Nudge the field sideways when an error appears, for a bit of tactile feedback.
		useEffect(() => {
			if (error) {
				shake();
			}
		}, [error, shake]);

		let labelColor = Colors.black;
		if (disabled) {
			labelColor = Colors.greyTextDark;
		} else if (focused) {
			labelColor = Colors.secondary;
		}

		return (
			<Pressable
				style={styles.container}
				onPress={() => {
					if (!disabled) innerRef.current?.focus();
				}}
			>
				<Animated.View
					style={[
						styles.inputWrapper,
						disabled ? styles.inputWrapperDisabled : null,
						focused && !disabled ? styles.inputWrapperFocused : null,
						showError ? styles.inputWrapperError : null,
						{ transform: [{ translateX: shakeX }] },
					]}
				>
					<Text style={[styles.inputLabel, { color: labelColor }]}>
						{label}
						{required ? <Text style={styles.required}> *</Text> : null}
					</Text>
					<TextInput
						placeholderTextColor={Colors.lightGrey}
						ref={setRef}
						style={disabled ? styles.disabledText : undefined}
						{...inputProps}
						onBlur={(e) => {
							setFocused(false);
							inputProps.onBlur?.(e);
						}}
						onFocus={(e) => {
							setFocused(true);
							inputProps.onFocus?.(e);
						}}
					/>
					{rightElement}
					{disabled && !rightElement ? <Ionicons name="lock-closed" size={18} style={styles.lockIcon} /> : null}
				</Animated.View>
				{showError ? (
					<View style={styles.errorRow}>
						<Ionicons color={Colors.redError} name="alert-circle" size={14} style={styles.errorIcon} />
						<Text style={styles.errorText}>{error}</Text>
					</View>
				) : null}
				{!showError && hint ? <Text style={styles.hint}>{hint}</Text> : null}
			</Pressable>
		);
	},
);

export default FormField;

const styles = StyleSheet.create({
	container: {
		marginHorizontal: Dimens.margin16,
		marginBottom: Dimens.margin16,
	},
	inputWrapper: {
		paddingHorizontal: Dimens.padding8,
		paddingVertical: Dimens.padding16,
		borderBottomWidth: 1,
		borderBottomColor: Colors.greyBorder,
	},
	// Read-only velden krijgen een gevulde, afgeronde "vergrendelde" look i.p.v. de open underline.
	inputWrapperDisabled: {
		backgroundColor: Colors.greyBgAlt,
		borderRadius: 8,
		borderBottomColor: Colors.transparent,
	},
	// The focus indicator this design calls for: the underline it already has, thickened and in the
	// accent colour. A browser draws a rectangular outline instead, which sits nowhere near the line
	// the field is drawn with; that one is switched off in public/index.html, so this is what a
	// keyboard user sees on every platform.
	inputWrapperFocused: {
		borderBottomWidth: 2,
		borderBottomColor: Colors.secondary,
		paddingBottom: Dimens.padding16 - 1,
	},
	inputWrapperError: {
		borderBottomColor: Colors.red,
	},
	inputLabel: {
		fontSize: Dimens.fontSize18,
		fontFamily: Fonts.montserratRegular,
		marginBottom: Dimens.margin8,
	},
	disabledText: {
		color: Colors.greyTextDark,
	},
	lockIcon: {
		position: 'absolute',
		right: Dimens.padding8,
		bottom: Dimens.padding16,
		color: Colors.greyTextDark,
	},
	required: {
		color: Colors.red,
	},
	errorRow: {
		flexDirection: 'row',
		alignItems: 'center',
		marginTop: Dimens.margin8,
		paddingHorizontal: Dimens.padding8,
	},
	errorIcon: {
		marginRight: Dimens.margin4,
	},
	errorText: {
		fontSize: Dimens.fontSize12,
		fontFamily: Fonts.montserratRegular,
		color: Colors.redError,
	},
	hint: {
		marginTop: Dimens.margin6,
		paddingHorizontal: Dimens.padding8,
		fontSize: Dimens.fontSize12,
		fontFamily: Fonts.montserratRegular,
		color: Colors.greyTextDark,
	},
});
